import { Box, Chip } from "@mui/material";
import { Reading, formatValue, muiColor } from "./pingReadings";

// MonitorSignalChips renders a kind's latest readings as a compact row of colored value chips for the
// monitor-list "Signal" column. It is the generic, per-kind signal renderer for kinds that don't have a
// bespoke component (cdc, system-monitor, a checkpoint plugin's signals). Each kind supplies
// a SignalKeySpec list describing which reading keys to show, their label, and the yellow/red bounds +
// direction used to color the chip — mirroring the kind's .cp convention so the chip matches the breaker.

export type SignalDirection = "ascending" | "descending" | "bool";

export interface SignalKeySpec {
  key: string; // reading key, e.g. "cdc.latency", "disk"
  label: string; // short chip prefix, e.g. "latency", "disk"
  direction: SignalDirection; // higher-worse | lower-worse | 0=bad
  yellow?: number;
  red?: number;
}

function levelFor(value: number, spec: SignalKeySpec): "red" | "yellow" | "green" {
  if (spec.direction === "bool") return value > 0 ? "green" : "red";
  if (spec.direction === "descending") {
    if (spec.red != null && value <= spec.red) return "red";
    if (spec.yellow != null && value <= spec.yellow) return "yellow";
    return "green";
  }
  // ascending (higher is worse)
  if (spec.red != null && value >= spec.red) return "red";
  if (spec.yellow != null && value >= spec.yellow) return "yellow";
  return "green";
}

// For a key with per-scope readings (e.g. disk per mount), keep the WORST (matching the breaker's
// worst-of), so one red mount colors the chip red.
function worstReading(readings: Reading[], spec: SignalKeySpec): Reading | undefined {
  const matches = readings.filter((r) => r.key === spec.key);
  if (matches.length === 0) return undefined;
  const rank = { red: 2, yellow: 1, green: 0 } as const;
  return matches.reduce((worst, r) =>
    rank[levelFor(r.value, spec)] > rank[levelFor(worst.value, spec)] ? r : worst,
  );
}

export interface MonitorSignalChipsProps {
  readings: Reading[] | undefined;
  keys: SignalKeySpec[];
}

export function MonitorSignalChips({ readings, keys }: MonitorSignalChipsProps) {
  if (!readings || readings.length === 0) return null;
  const chips = keys
    .map((spec) => {
      const r = worstReading(readings, spec);
      if (r == null) return null;
      const level = levelFor(r.value, spec);
      // A bool (e.g. reachable) only renders when it's the BAD value ("down") — a healthy bool is
      // redundant with the numeric readings (e.g. a green latency already implies reachable). Surface
      // it only on failure, where there's often no numeric reading to carry the signal.
      if (spec.direction === "bool") {
        if (r.value > 0) return null;
        return { key: spec.key, text: `${spec.label} down`.trim(), color: muiColor(level) };
      }
      const text = `${spec.label} ${formatValue(r.value, r.unit)}`;
      return { key: spec.key, text, color: muiColor(level) };
    })
    .filter((c): c is { key: string; text: string; color: ReturnType<typeof muiColor> } => c != null);

  if (chips.length === 0) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, height: "100%", flexWrap: "wrap" }}>
      {chips.map((c) => (
        // Muted: outlined variant (colored border + text, transparent fill) so Signal stays SECONDARY to
        // the filled Status column — matching PingSignalChips. Status owns the bright filled palette.
        <Chip key={c.key} size="small" label={c.text} color={c.color} variant="outlined" />
      ))}
    </Box>
  );
}
